<!-- One day of the paper trail, above the Now rule on Today's feed.

     The feed reads down through time, so what the pad said yesterday belongs
     ON this screen rather than a tab away in History → Proposed. The record was
     always there — every proposal the app printed is settled overnight from the
     closing prices whether or not anyone backed it — but it was somewhere the
     punter had to go looking, and a line of small print ("Yesterday: 3 of 8
     landed · See them ›") is a summary, not the tickets.

     READ-ONLY, AND VISIBLY SO. There is nothing to place: these matches have
     been and gone. So the day carries no Place bet, no Copy legs and no ⓘ —
     just the tickets, dimmed to the same .55 an expired ticket takes, with the
     stamp the trail gave each one in FULL ink. Dimming the whole sheet and
     keeping the stamp bright is the point: the paper recedes, the verdict does
     not, and somebody scrolling up is scrolling up to read verdicts.

     The row is derived by `proposalRow`, the same function History renders its
     Proposed pile through, so a ticket cannot say "Won" here and "No result"
     there. -->
<template>
  <section class="proposed-day">
    <h2 class="section-title section-title--ruled day-rule">{{ label }}</h2>

    <div v-if="rows.length" class="sheet day-sheet">
      <article v-for="r in rows" :key="r.key" class="day-entry">
        <Ticket
          :multi="r.multi"
          :legs="r.legs"
          :now="now"
          :eyebrow="r.eyebrow"
          :stamp="r.stamp"
          :stamp-tone="r.tone"
          heading-tag="h3"
          density="feed"
          compact
        />
      </article>
    </div>

    <p v-else class="small-print day-empty">Nothing was proposed.</p>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import Ticket from './Ticket.vue';
import { proposalRow } from '../lib/history-view.js';

const props = defineProps({
  /** The day's heading — "Yesterday", "Sat 12 Sep". */
  label: { type: String, required: true },
  /** That day's settled proposals, as `stores/paper-trail.js` hands them over. */
  proposals: { type: Array, default: () => [] },
  /** Epoch ms. Every leg here has started, so this only drives their labels. */
  now: { type: Number, default: () => Date.now() },
});

// Only the three that are not the plain red stamp get an ink of their own —
// LOST and VOID keep `--stamp`, which is the app's one red and is reserved for
// what the money actually did. Same map as `ProposedStub`.
const TONE = { won: 'won', pending: 'pending', unresulted: 'unresulted' };

const rows = computed(() => (props.proposals ?? [])
  .filter((p) => p?.multi)
  .map((p, i) => {
    const row = proposalRow(p, { now: props.now });
    return {
      key: p.id ?? `${row.forDate}-${i}`,
      multi: p.multi,
      legs: p.legs ?? [],
      eyebrow: row.strategy || p.strategyName || '',
      stamp: row.resultLabel,
      tone: TONE[row.result] ?? '',
    };
  }));
</script>

<style scoped>
/* The day's own heading is a rule across the desk with the day under it — the
   same treatment the Now rule takes, because they are the same kind of thing:
   a boundary between two stretches of time, not a label on the sheet below. */
.day-rule {
  margin-top: var(--sp-5);
}

.day-sheet {
  margin: 0;
  padding: var(--sp-3);
}

.day-sheet > :last-child {
  margin-bottom: 0;
}

/* QUIETER, NOT GONE — the same .55 an expired ticket takes on the live half of
   the feed, and for the same reason. Opacity rather than a second set of inks
   so every stamp stays the ink it earned. */
.day-entry {
  opacity: 0.55;
}

.day-empty {
  margin: var(--sp-2) 0 0;
}
</style>
